import { createContext, useContext, useState, useEffect, useCallback } from "react";
import itTranslations from "../locales/it.json";
import hrTranslations from "../locales/hr.json";
import slTranslations from "../locales/sl.json";

const translations = { it: itTranslations, hr: hrTranslations, sl: slTranslations };
const SUPPORTED_LANGS = ["it", "hr", "sl"];
const LANG_LABELS = { it: "Italiano", hr: "Hrvatski", sl: "Slovensko" };

const I18nContext = createContext({ t: (key) => key, lang: "it" });

function getNestedValue(obj, path) {
  return path.split(".").reduce((curr, key) => curr?.[key], obj);
}

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      const tenant = JSON.parse(localStorage.getItem("tenant") || "{}");
      return SUPPORTED_LANGS.includes(tenant.lingua) ? tenant.lingua : "it";
    } catch {
      return "it";
    }
  });

  useEffect(() => {
    const onStorage = () => {
      try {
        const tenant = JSON.parse(localStorage.getItem("tenant") || "{}");
        const newLang = SUPPORTED_LANGS.includes(tenant.lingua) ? tenant.lingua : "it";
        setLang(newLang);
      } catch { /* ignore */ }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const t = useCallback((key, fallback) => {
    const val = getNestedValue(translations[lang], key);
    if (val !== undefined) return val;
    // Fallback to Italian
    const itVal = getNestedValue(translations.it, key);
    if (itVal !== undefined) return itVal;
    return fallback || key;
  }, [lang]);

  const changeLang = useCallback((newLang) => {
    if (SUPPORTED_LANGS.includes(newLang)) {
      setLang(newLang);
      try {
        const tenant = JSON.parse(localStorage.getItem("tenant") || "{}");
        tenant.lingua = newLang;
        localStorage.setItem("tenant", JSON.stringify(tenant));
      } catch { /* ignore */ }
    }
  }, []);

  return (
    <I18nContext.Provider value={{ t, lang, changeLang }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  return useContext(I18nContext);
}

export { SUPPORTED_LANGS, LANG_LABELS };
